/* ═══════════════════════════════════════════════════════════════
 * VOLUME PROFILE — canvas-overlay boven de chart.
 * 1:1 poort van TERMINAL.volumeProfile uit terminal_wyckoffpcg.html.
 * Blijft buiten React's render-cyclus (canvas + rAF), exact zoals
 * het origineel: performance-kritisch, dus geen DOM-diffing per frame.
 * ═══════════════════════════════════════════════════════════════ */

import { CONFIG as CFG } from "./config";

const C = CFG.colors;

// container: het DOM-element waar de chart in hangt (chartApi's container)
// getApi: () => { chart, candleSeries }
// getCandles: () => huidige candle-array
export function createVolumeProfile(container, getApi, getCandles) {
  const canvas = document.createElement("canvas");
  canvas.className = "vp-overlay";
  Object.assign(canvas.style, {
    position: "absolute", inset: "0", pointerEvents: "none", zIndex: "3",
  });
  container.style.position = "relative";
  container.appendChild(canvas);
  const ctx = canvas.getContext("2d");

  let rafPending = false;
  let stats = null;
  let rightInset = 0;
  let onStats = null;
  let visible = true;

  function sizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    const w = container.clientWidth, h = container.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = w + "px";
    canvas.style.height = h + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function compute() {
    const { chart } = getApi();
    const candles = getCandles();
    if (!candles || !candles.length) return null;

    const lr = chart.timeScale().getVisibleLogicalRange();
    let from = 0, to = candles.length - 1;
    if (lr) {
      from = Math.max(0, Math.floor(lr.from));
      to = Math.min(candles.length - 1, Math.ceil(lr.to));
    }
    if (to <= from) return null;

    let lo = Infinity, hi = -Infinity;
    for (let i = from; i <= to; i++) {
      if (candles[i].low < lo) lo = candles[i].low;
      if (candles[i].high > hi) hi = candles[i].high;
    }
    if (!isFinite(lo) || hi <= lo) return null;

    const N = CFG.vpBuckets, step = (hi - lo) / N;
    const buckets = new Float64Array(N);

    for (let i = from; i <= to; i++) {
      const cd = candles[i];
      const b0 = Math.max(0, Math.floor((cd.low - lo) / step));
      const b1 = Math.min(N - 1, Math.floor((cd.high - lo) / step));
      const span = b1 - b0 + 1;
      const per = cd.volume / span;
      for (let b = b0; b <= b1; b++) buckets[b] += per;
    }

    let poc = 0, maxV = 0, total = 0;
    for (let b = 0; b < N; b++) {
      total += buckets[b];
      if (buckets[b] > maxV) { maxV = buckets[b]; poc = b; }
    }

    let acc = buckets[poc], up = poc + 1, dn = poc - 1;
    const target = total * CFG.vpValueArea;
    let vahB = poc, valB = poc;
    while (acc < target && (up < N || dn >= 0)) {
      const vUp = up < N ? buckets[up] : -1;
      const vDn = dn >= 0 ? buckets[dn] : -1;
      if (vUp >= vDn) { acc += Math.max(0, vUp); vahB = up; up++; }
      else { acc += Math.max(0, vDn); valB = dn; dn--; }
    }

    const price = (b) => lo + (b + 0.5) * step;
    return {
      buckets, N, maxV, step, lo, hi,
      pocPrice: price(poc),
      vahPrice: price(Math.max(vahB, poc)),
      valPrice: price(Math.min(valB, poc)),
    };
  }

  function draw() {
    rafPending = false;
    sizeCanvas();
    const w = container.clientWidth, h = container.clientHeight;
    ctx.clearRect(0, 0, w, h);
    if (!visible) { stats = null; if (onStats) onStats(null); return; }

    const p = compute();
    if (!p) { stats = null; if (onStats) onStats(null); return; }
    const { chart, candleSeries } = getApi();

    const axisW = (chart.priceScale("right").width && chart.priceScale("right").width()) || 64;
    const plotRight = w - axisW - 2 - rightInset;
    const maxBarW = w * CFG.vpWidthFrac;

    const yOf = (pr) => candleSeries.priceToCoordinate(pr);
    const inVA = (pr) => pr >= p.valPrice && pr <= p.vahPrice;

    if (CFG.vpShowBars) {
      for (let b = 0; b < p.N; b++) {
        const v = p.buckets[b]; if (v <= 0) continue;
        const pr = p.lo + (b + 0.5) * p.step;
        const y = yOf(pr); if (y == null) continue;
        const yNext = yOf(pr + p.step);
        const th = yNext == null ? 2 : Math.max(1, Math.abs(y - yNext) - 0.5);
        const len = (v / p.maxV) * maxBarW;
        const isPoc = Math.abs(pr - p.pocPrice) <= p.step * 0.5;

        ctx.fillStyle = isPoc ? "rgba(240,185,11,0.30)"
          : inVA(pr) ? C.vpFillVA : C.vpFill;
        ctx.fillRect(plotRight - len, y - th / 2, len, th);
      }
    }

    ctx.save();
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    [["VAH", p.vahPrice], ["VAL", p.valPrice]].forEach(([lbl, pr]) => {
      const y = yOf(pr); if (y == null) return;
      ctx.strokeStyle = "rgba(240,185,11,0.45)";
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(plotRight, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(240,185,11,0.85)";
      ctx.font = "600 9px 'JetBrains Mono',monospace";
      ctx.fillText(lbl, plotRight - maxBarW + 2, y - 3);
      ctx.setLineDash([4, 3]);
    });
    ctx.restore();

    const yPoc = yOf(p.pocPrice);
    if (yPoc != null) {
      ctx.strokeStyle = C.poc; ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.moveTo(0, yPoc); ctx.lineTo(plotRight, yPoc); ctx.stroke();

      const txt = "vPOC";
      ctx.font = "700 9px 'JetBrains Mono',monospace";
      const tw = ctx.measureText(txt).width + 8;
      ctx.fillStyle = C.poc;
      ctx.fillRect(plotRight - maxBarW - tw, yPoc - 7, tw, 13);
      ctx.fillStyle = "#0b0e11";
      ctx.fillText(txt, plotRight - maxBarW - tw + 4, yPoc + 3);
    }

    stats = { poc: p.pocPrice, vah: p.vahPrice, val: p.valPrice };
    if (onStats) onStats(stats);
  }

  function scheduleRedraw() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(draw);
  }

  function destroy() {
    try { canvas.remove(); } catch {}
  }

  return {
    redraw: scheduleRedraw,
    getStats: () => stats,
    onStats: (cb) => { onStats = cb; },
    setRightInset: (px) => { rightInset = px; scheduleRedraw(); },
    setVisible: (v) => { visible = v; scheduleRedraw(); },
    isVisible: () => visible,
    destroy,
  };
}
